import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createDepartment, listDepartments, updateDepartment } from '@/api/departments';
import { ApiError } from '@/api/client';
import type { Department, DepartmentWrite } from '@/types/oscar';
import {
  composeDepartmentCode,
  departmentSchema,
  geographicLabel,
  geographicOptions,
  suggestNextSequence,
  type DepartmentFormData,
} from './schema';

/** Sentinel option value for "add a new code" in the BU / sub dropdowns. */
const NEW = '__new__';

const emptyDefaults: DepartmentFormData = {
  DepartmentCode: '',
  DepartmentDescription: '',
  GeographicCode: 'B',
  GeographicDescription: 'Branch',
  BusinessUnitCode: '',
  BusinessUnitDescription: '',
  BUSubCode: '',
  BUSubDescription: '',
};

function toPayload(data: DepartmentFormData): DepartmentWrite {
  return {
    DepartmentCode: data.DepartmentCode.trim().toUpperCase(),
    DepartmentDescription: data.DepartmentDescription.trim(),
    GeographicCode: data.GeographicCode,
    GeographicDescription: data.GeographicDescription.trim(),
    BusinessUnitCode: data.BusinessUnitCode.trim().toUpperCase(),
    BusinessUnitDescription: data.BusinessUnitDescription.trim(),
    BUSubCode: data.BUSubCode.trim().toUpperCase(),
    BUSubDescription: data.BUSubDescription.trim(),
  };
}

/** Distinct {code -> description} pairs from a list, sorted by description. */
function distinctCodes(pairs: Array<[string | null, string | null]>): Array<{ code: string; desc: string }> {
  const map = new Map<string, string>();
  for (const [code, desc] of pairs) {
    if (code && !map.has(code)) map.set(code, desc ?? '');
  }
  return [...map.entries()].map(([code, desc]) => ({ code, desc })).sort((a, b) => a.desc.localeCompare(b.desc));
}

/**
 * Department create/edit modal — the "code builder" (PR-27, refined).
 *
 * Create: the DepartmentCode is *composed* read-only from Geographic scope +
 * Business unit code + Sub-unit code + a 2-digit sequence (auto-suggested,
 * editable). Business unit and sub codes are chosen from dropdowns of existing
 * values, or "Add new…" to introduce one. Descriptions auto-fill from the
 * chosen code.
 *
 * Edit: the code is immutable, so the parts that make it up (Geo / BU / Sub
 * codes) are locked too; only the descriptions are editable. Retire + recreate
 * to fix a wrong code.
 */
export function DepartmentForm({
  open,
  onOpenChange,
  editing,
  departments,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The department to edit, or null to create. */
  editing: Department | null;
  /** Existing departments — source for the BU / sub dropdowns + NN suggestion. */
  departments: Department[];
  onSaved?: () => void;
}) {
  const isEdit = editing !== null;
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  // "existing" = pick from the dropdown; "new" = type a fresh code + description.
  const [buMode, setBuMode] = useState<'existing' | 'new'>('existing');
  const [subMode, setSubMode] = useState<'existing' | 'new'>('existing');
  const [seq, setSeq] = useState('01');

  const form = useForm<DepartmentFormData>({
    resolver: zodResolver(departmentSchema),
    defaultValues: emptyDefaults,
  });
  const { register, handleSubmit, reset, setValue, watch, formState } = form;

  const geo = watch('GeographicCode');
  const bu = watch('BusinessUnitCode');
  const sub = watch('BUSubCode');

  // All codes including retired ones — a retired code still collides on the
  // primary key, so both the next-sequence suggestion and the duplicate guard
  // must consider them. Fetched only while creating.
  const allDeptsQuery = useQuery({
    queryKey: ['departments', { includeDeleted: true }],
    queryFn: ({ signal }) => listDepartments(true, signal),
    enabled: open && !isEdit,
  });
  const allCodes = useMemo(
    () => (allDeptsQuery.data ?? departments).map((d) => d.DepartmentCode),
    [allDeptsQuery.data, departments],
  );
  const buOptions = useMemo(
    () => distinctCodes(departments.map((d) => [d.BusinessUnitCode, d.BusinessUnitDescription])),
    [departments],
  );
  // Sub codes seen under the currently-selected BU (subs are BU-specific).
  const subOptions = useMemo(
    () =>
      distinctCodes(
        departments
          .filter((d) => d.BusinessUnitCode?.toUpperCase() === bu?.toUpperCase())
          .map((d) => [d.BUSubCode, d.BUSubDescription]),
      ),
    [departments, bu],
  );

  // Populate / reset the form each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    if (editing) {
      reset({
        DepartmentCode: editing.DepartmentCode,
        DepartmentDescription: editing.DepartmentDescription,
        GeographicCode: (editing.GeographicCode as DepartmentFormData['GeographicCode']) ?? 'B',
        GeographicDescription: editing.GeographicDescription,
        BusinessUnitCode: editing.BusinessUnitCode,
        BusinessUnitDescription: editing.BusinessUnitDescription,
        BUSubCode: editing.BUSubCode ?? '',
        BUSubDescription: editing.BUSubDescription ?? '',
      });
      setBuMode('existing');
      setSubMode('existing');
      setSeq(editing.DepartmentCode.slice(-2));
    } else {
      reset(emptyDefaults);
      setBuMode('existing');
      setSubMode('existing');
      setSeq('01');
    }
  }, [open, editing, reset]);

  // Create mode only: keep the composed code + suggested sequence in step with
  // the chosen parts. (Edit mode never recomposes — the code is immutable.)
  useEffect(() => {
    if (isEdit || !open) return;
    if (geo && bu && sub) setSeq(suggestNextSequence(allCodes, geo, bu, sub));
  }, [isEdit, open, geo, bu, sub, allCodes]);

  useEffect(() => {
    if (isEdit || !open) return;
    const paddedSeq = seq ? seq.padStart(2, '0') : '';
    setValue('DepartmentCode', composeDepartmentCode(geo, bu, sub, paddedSeq), { shouldValidate: !!(geo && bu && sub && seq) });
  }, [isEdit, open, geo, bu, sub, seq, setValue]);

  const mutation = useMutation({
    mutationFn: (data: DepartmentFormData) =>
      isEdit ? updateDepartment(editing!.DepartmentCode, toPayload(data)) : createDepartment(toPayload(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      onSaved?.();
      onOpenChange(false);
    },
    onError: (err) => setSubmitError(err instanceof ApiError ? err.message : 'Could not save the department code.'),
  });

  const composedCode = watch('DepartmentCode');
  // Belt-and-braces client guard — the API also blocks a duplicate (409), but
  // catch it before submit so the sequence can be nudged.
  const isDuplicate =
    !isEdit && composedCode.length > 0 && allCodes.some((c) => c.toUpperCase() === composedCode.toUpperCase());

  const onSubmit = (data: DepartmentFormData) => {
    if (isDuplicate) return;
    setSubmitError(null);
    mutation.mutate(data);
  };

  // Selecting a BU from the dropdown: fill its description and reset the sub
  // (subs are BU-specific). "Add new" clears the fields for manual entry.
  const onBuSelect = (value: string) => {
    if (value === NEW) {
      setBuMode('new');
      setValue('BusinessUnitCode', '');
      setValue('BusinessUnitDescription', '');
    } else {
      setBuMode('existing');
      setValue('BusinessUnitCode', value);
      setValue('BusinessUnitDescription', buOptions.find((o) => o.code === value)?.desc ?? '');
    }
    // Any BU change invalidates the sub selection.
    setSubMode('existing');
    setValue('BUSubCode', '');
    setValue('BUSubDescription', '');
  };

  const onSubSelect = (value: string) => {
    if (value === NEW) {
      setSubMode('new');
      setValue('BUSubCode', '');
      setValue('BUSubDescription', '');
    } else {
      setSubMode('existing');
      setValue('BUSubCode', value);
      setValue('BUSubDescription', subOptions.find((o) => o.code === value)?.desc ?? '');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[min(94vw,640px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-md bg-white p-5 shadow-xl ring-1 ring-black/5 focus:outline-none">
          <Dialog.Title className="font-heading text-lg font-bold text-navy">
            {isEdit ? 'Edit department code' : 'Add department code'}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-darkGrey/80">
            {isEdit
              ? 'The department code and its parts cannot be changed — retire and recreate to fix a wrong code. You can update the descriptions.'
              : 'The code is built for you from the parts below (Geo-BU-Sub-NN).'}
          </Dialog.Description>

          {submitError && (
            <div role="alert" className="mt-3 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
              {submitError}
            </div>
          )}

          {/* Composed code preview — always read-only (the builder owns it). */}
          <div className="mt-4 rounded border border-border bg-bg/50 px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-darkGrey/60">Department code</span>
            <div className="font-mono text-lg font-semibold text-navy">
              {(isEdit ? editing!.DepartmentCode : composedCode) || <span className="text-darkGrey/40">B-IST-BOT-01</span>}
            </div>
            {!isEdit && isDuplicate && (
              <span className="mt-1 block text-xs text-red">
                This department code already exists — change the sequence number.
              </span>
            )}
            {!isEdit && !isDuplicate && formState.errors.DepartmentCode && (geo && bu && sub) && (
              <span className="mt-1 block text-xs text-red">{formState.errors.DepartmentCode.message}</span>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Geographic scope */}
              <Field label="Geographic scope" error={formState.errors.GeographicCode?.message}>
                {isEdit ? (
                  <ReadOnly value={`${geographicLabel(geo)} (${geo})`} />
                ) : (
                  <select
                    {...register('GeographicCode', {
                      onChange: (e) => setValue('GeographicDescription', geographicLabel(e.target.value)),
                    })}
                    className={inputCls}
                  >
                    {geographicOptions.map((o) => (
                      <option key={o.code} value={o.code}>{o.label} ({o.code})</option>
                    ))}
                  </select>
                )}
              </Field>

              {/* Business unit code */}
              <Field label="Business unit code" error={formState.errors.BusinessUnitCode?.message}>
                {isEdit ? (
                  <ReadOnly value={bu} />
                ) : (
                  <select
                    aria-label="Business unit code"
                    value={buMode === 'new' ? NEW : bu}
                    onChange={(e) => onBuSelect(e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Choose…</option>
                    {buOptions.map((o) => (
                      <option key={o.code} value={o.code}>{o.code} — {o.desc}</option>
                    ))}
                    <option value={NEW}>+ Add new business unit…</option>
                  </select>
                )}
              </Field>

              {/* Business unit description — auto-filled unless adding new / editing */}
              <Field label="Business unit description" error={formState.errors.BusinessUnitDescription?.message}>
                {!isEdit && buMode === 'existing' ? (
                  <ReadOnly value={watch('BusinessUnitDescription')} />
                ) : (
                  <input {...register('BusinessUnitDescription')} className={inputCls} />
                )}
              </Field>

              {/* New BU code input (create + add-new only) */}
              {!isEdit && buMode === 'new' && (
                <Field label="New business unit code (3 letters)" error={formState.errors.BusinessUnitCode?.message}>
                  <input
                    {...register('BusinessUnitCode', { onChange: (e) => setValue('BusinessUnitCode', e.target.value.toUpperCase()) })}
                    placeholder="IST"
                    maxLength={3}
                    className={inputCls}
                  />
                </Field>
              )}

              {/* Sub-unit code */}
              <Field label="Sub-unit code" error={formState.errors.BUSubCode?.message}>
                {isEdit ? (
                  <ReadOnly value={sub} />
                ) : (
                  <select
                    aria-label="Sub-unit code"
                    value={subMode === 'new' ? NEW : sub}
                    onChange={(e) => onSubSelect(e.target.value)}
                    disabled={!bu && buMode === 'existing'}
                    className={inputCls}
                  >
                    <option value="">Choose…</option>
                    {subOptions.map((o) => (
                      <option key={o.code} value={o.code}>{o.code} — {o.desc}</option>
                    ))}
                    <option value={NEW}>+ Add new sub-unit…</option>
                  </select>
                )}
              </Field>

              {/* Sub-unit description */}
              <Field label="Sub-unit description" error={formState.errors.BUSubDescription?.message}>
                {!isEdit && subMode === 'existing' ? (
                  <ReadOnly value={watch('BUSubDescription')} />
                ) : (
                  <input {...register('BUSubDescription')} className={inputCls} />
                )}
              </Field>

              {/* New sub code input (create + add-new only) */}
              {!isEdit && subMode === 'new' && (
                <Field label="New sub-unit code (3 letters)" error={formState.errors.BUSubCode?.message}>
                  <input
                    {...register('BUSubCode', { onChange: (e) => setValue('BUSubCode', e.target.value.toUpperCase()) })}
                    placeholder="BOT"
                    maxLength={3}
                    className={inputCls}
                  />
                </Field>
              )}

              {/* Sequence — create only; auto-suggested, editable */}
              {!isEdit && (
                <Field label="Sequence (NN)">
                  <input
                    aria-label="Sequence number"
                    value={seq}
                    onChange={(e) => setSeq(e.target.value.replace(/\D/g, '').slice(0, 2))}
                    onBlur={(e) => setSeq(e.target.value ? e.target.value.padStart(2, '0') : '')}
                    inputMode="numeric"
                    maxLength={2}
                    className={inputCls}
                  />
                  <span className="mt-1 block font-normal text-darkGrey/60">Auto-suggested; change if you need a different number.</span>
                </Field>
              )}
            </div>

            {/* Department description — last, so it's the final thing entered. */}
            <Field label="Department description" error={formState.errors.DepartmentDescription?.message}>
              <input {...register('DepartmentDescription')} className={inputCls} />
            </Field>

            <div className="flex justify-end gap-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded border border-border bg-white px-4 py-2 text-sm font-medium text-darkGrey hover:bg-bg"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={mutation.isPending || (!isEdit && (isDuplicate || !composedCode))}
                className="rounded bg-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create department'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm';

function ReadOnly({ value }: { value?: string }) {
  return (
    <div className="mt-1 w-full rounded border border-border bg-bg px-2 py-1.5 text-sm text-darkGrey/70">
      {value || '—'}
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs font-semibold text-darkGrey">
      {label}
      {children}
      {error && <span className="mt-1 block font-normal text-red">{error}</span>}
    </label>
  );
}
